import { TablePage, Dialog } from 'web-mojo';
import { FirmwareAssignmentList } from '@portal/models/FirmwareAssignment.js';

export default class FirmwareAssignmentsPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Firmware Assignments',
            icon: 'bi-sliders',
            pageName: 'firmware_assignments',
            route: 'group/firmware',
            permissions: ['manage_firmware', 'manage_cam_devices', 'view_cam_devices', 'manage_group', 'manage_groups'],
            requiresGroup: true,

            Collection: FirmwareAssignmentList,

            defaultQuery: {
                sort: '-modified',
            },

            columns: [
                {
                    label: 'Target',
                    key: 'device',
                    formatter: (val, row) => {
                        const dev = row?.device;
                        if (!dev) return '<span class="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle">Entire group</span>';
                        if (typeof dev === 'object') return dev.name || dev.uuid || `Device #${dev.id}`;
                        return `Device #${dev}`;
                    },
                },
                {
                    label: 'Channel',
                    key: 'channel',
                    formatter: (val) => {
                        if (!val) return '<span class="text-muted small">—</span>';
                        const map = { stable: 'success', beta: 'info', dev: 'secondary' };
                        return `<span class="badge bg-${map[val] || 'secondary'}">${val}</span>`;
                    },
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Stable', value: 'stable' },
                            { label: 'Beta', value: 'beta' },
                            { label: 'Dev', value: 'dev' },
                        ]
                    },
                },
                {
                    label: 'Version Pin',
                    key: 'version',
                    formatter: (val) => val
                        ? `<span class="font-monospace">${val}</span>`
                        : '<span class="text-muted small fst-italic">track channel</span>',
                },
                {
                    label: 'Updated',
                    key: 'modified',
                    formatter: 'relative',
                    visibility: 'md',
                },
            ],

            emptyMessage: 'No firmware assignments yet. Add one to declare which channel or version this group should run.',

            showAdd: true,
            searchable: false,
            sortable: true,
            filterable: true,
            paginated: true,
            showRefresh: true,

            contextMenu: [
                { label: 'Edit', icon: 'bi bi-pencil', action: 'edit' },
                { label: 'Roll out now', icon: 'bi bi-rocket-takeoff', action: 'rollout' },
                { label: 'Delete', icon: 'bi bi-trash', action: 'delete', className: 'text-danger' },
            ],

            tableViewOptions: {
                hideActivePillNames: ['group'],
                addRequiresActiveGroup: true,
                addButtonLabel: 'Add Assignment',
            },

            tableOptions: {
                striped: false,
                bordered: false,
                hover: true,
                responsive: true,
            },

            ...options,
        });
    }

    async onActionRollout(event, el) {
        const app = this.getApp();
        const id = el.getAttribute('data-id');
        const assignment = this.collection.get(id);
        if (!assignment) return;

        // Device rows target that one device; the group-wide row targets the group.
        const dev = assignment.get('device');
        const grp = assignment.get('group');
        const groupId = (typeof grp === 'object' ? grp?.id : grp) || app.groupId();
        const target = dev
            // Scope-only group so a member-tier manage_firmware grant resolves;
            // the device id stays the dispatch target (see DeviceFirmwareView).
            ? { device: (typeof dev === 'object' ? dev.id : dev), group: groupId }
            : { group: groupId };

        // Dry-run preview at max limit — would_send consumes limit exactly like
        // sent, so the server default of 25 would preview only one batch.
        // Dry-run carries queue:true so offline members preview as would_queue;
        // an old server ignores the unknown field (mojo REST standard) and never
        // returns a `queued` count — that absence is the capability probe below.
        app.modal.loading('Checking what would update...');
        let preview;
        try {
            preview = await app.rest.POST('/api/firmware/ota/trigger', { ...target, dry_run: true, limit: 200, queue: true });
        } finally {
            app.modal.hideLoading();
        }
        if (!preview.success || !preview.data.status) {
            app.toast.error(preview.data?.error || 'Failed to check rollout');
            return;
        }
        const dry = preview.data.data;
        // Capability probe: the server includes `queued` in its envelope only when
        // it honored the flag. Preview and execution read the same signal, so the
        // real dispatch never sends queue:true to a server that can't queue.
        const canQueue = 'queued' in dry;
        const queued = dry.queued || 0;

        const nothingToSend = !dry.sent && !queued;
        const buttons = nothingToSend
            ? [{ text: 'Close', class: 'btn-secondary', dismiss: true }]
            : [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: `Send Updates (${dry.sent} now${queued ? `, ${queued} queued` : ''})`, value: 'go', class: 'btn-primary', icon: 'bi bi-rocket-takeoff' },
            ];

        const choice = await Dialog.showDialog({
            title: 'Rollout Preview',
            body: this._renderPreview(dry, !!dev),
            size: 'lg',
            scrollable: true,
            buttons,
        });
        if (choice !== 'go') return;

        // Send queue:true only when the preview proved the server supports it, so
        // an old server never sees the flag on a live dispatch.
        const response = await app.rest.POST('/api/firmware/ota/trigger', { ...target, ...(canQueue ? { queue: true } : {}) });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to trigger rollout');
            return;
        }
        const res = response.data.data;
        // Mention queueing only when the server actually queued members, so an old
        // server (no `queued`) never yields a "queued 0" claim about a feature it lacks.
        const queuedNote = res.queued ? `, queued ${res.queued} for reconnect` : '';
        app.toast.success(`Dispatched ${res.sent} update${res.sent === 1 ? '' : 's'}${queuedNote} (${res.skipped} skipped)`);
        if ((res.results || []).some(r => r.reason === 'pending')) {
            app.toast.info('Some devices are cooling down from a recent dispatch — run again in ~10 minutes to continue the rollout.');
        }
    }

    _renderPreview(dry, isDeviceTarget) {
        const rows = (dry.results || []).map(r => {
            const label = r.uuid ? `<span class="font-monospace small">${r.uuid}</span>` : `Device #${r.device}`;
            let detail = '';
            if (r.action === 'would_send' || r.action === 'would_queue') {
                detail = r.version ? `v${r.version}` : '';
            } else if (r.action === 'skipped') {
                detail = r.reason || '';
            } else if (r.action === 'not_found') {
                detail = 'unknown device';
            }
            const cls = { would_send: 'text-success fw-semibold', would_queue: 'text-warning fw-semibold', skipped: 'text-muted', not_found: 'text-danger' }[r.action] || '';
            return `<tr><td>${label}</td><td class="${cls} small">${(r.action || '').replace('_', ' ')}</td><td class="small">${detail}</td></tr>`;
        }).join('');

        const batchNote = isDeviceTarget ? '' : `
            <div class="text-muted small mt-2">
                Showing this batch only (${(dry.results || []).length} device${(dry.results || []).length === 1 ? '' : 's'}, limit ${dry.limit}).
                Sending dispatches in batches of 25 — re-run the rollout until nothing is left to send.
            </div>`;

        return `
            <div>
                <div class="mb-2">
                    <span class="badge bg-success me-1">${dry.sent} would update</span>
                    ${dry.queued > 0 ? `<span class="badge bg-warning text-dark me-1">${dry.queued} will queue on reconnect</span>` : ''}
                    <span class="badge bg-secondary">${dry.skipped} skipped</span>
                </div>
                <div class="table-responsive" style="max-height: 320px; overflow-y: auto;">
                    <table class="table table-sm align-middle mb-0">
                        <thead><tr><th>Device</th><th>Result</th><th>Detail</th></tr></thead>
                        <tbody>${rows || '<tr><td colspan="3" class="text-muted small">No devices matched.</td></tr>'}</tbody>
                    </table>
                </div>
                ${batchNote}
            </div>`;
    }
}
